/**
 * DevPasswordProtection - A client-side password protection component for dev environment only
 * Only activated in deployed dev environment (not local development or production)
 * Uses SHA-256 hashing to verify password and stores auth state in sessionStorage
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

// Environment detection logic
const getEnvironmentType = (): 'local' | 'dev' | 'production' => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // Local development (localhost, 127.0.0.1, or file protocol)
  if (hostname === 'localhost' || 
      hostname === '127.0.0.1' || 
      hostname.startsWith('192.168.') || 
      hostname.startsWith('10.') || 
      protocol === 'file:') {
    return 'local';
  }
  
  // Dev environment (contains 'dev', 'staging', or 'test' in hostname)
  if (hostname.includes('dev') || 
      hostname.includes('staging') || 
      hostname.includes('test') ||
      hostname.includes('preview')) {
    return 'dev';
  }
  
  // Everything else is production
  return 'production';
};

// Client-side password hashing using Web Crypto API
const hashPassword = async (password: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// Known password hash (SHA-256 of "dev2024!")
// Change this to your desired password by running: echo -n "yourpassword" | shasum -a 256
const KNOWN_PASSWORD_HASH = '539406a4bf6f5d22633bfbfc43db1448350135e9385d2b8d2137904e0702c3bf';

const DEV_AUTH_KEY = 'rainfactory_dev_authenticated';

interface DevPasswordProtectionProps {
  children: React.ReactNode;
}

const DevPasswordForm: React.FC<{ onAuthenticated: () => void }> = ({ onAuthenticated }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password.trim()) {
      toast({
        title: "Password required",
        description: "Please enter the dev environment password",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const hashedInput = await hashPassword(password.trim());
      
      if (hashedInput === KNOWN_PASSWORD_HASH) {
        // Store authentication in sessionStorage (cleared when tab closes)
        sessionStorage.setItem(DEV_AUTH_KEY, 'true');
        onAuthenticated();
        
        toast({
          title: "Access granted",
          description: "Welcome to the dev environment"
        });
      } else {
        toast({
          title: "Access denied",
          description: "Incorrect password",
          variant: "destructive"
        });
        setPassword(''); // Clear the password field
      }
    } catch (error) {
      console.error('Password verification error:', error);
      toast({
        title: "Error",
        description: "Failed to verify password",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold">Dev Environment Access</CardTitle>
          <CardDescription>
            This is a protected development environment. Please enter the password to continue.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter development password"
                  disabled={isLoading}
                  className="pr-10"
                  autoComplete="off"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading || !password.trim()}
            >
              {isLoading ? 'Verifying...' : 'Access Environment'}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Environment: <span className="font-semibold text-orange-600">Development</span>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const DevPasswordProtection: React.FC<DevPasswordProtectionProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const envType = getEnvironmentType();
    
    // Only enable password protection in dev environment
    if (envType !== 'dev') {
      setIsAuthenticated(true);
      return;
    }

    // Check if already authenticated in this session
    const authStatus = sessionStorage.getItem(DEV_AUTH_KEY);
    setIsAuthenticated(authStatus === 'true');
  }, []);

  // Show loading state while determining environment
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If not in dev environment or already authenticated, render children
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // Show password form for unauthenticated dev environment access
  return (
    <DevPasswordForm 
      onAuthenticated={() => setIsAuthenticated(true)} 
    />
  );
};

export default DevPasswordProtection;